<script setup lang="ts">
import {
  requestAccessAsExistingCustomer,
  type RequestAccessAsExistingCustomerRequest,
} from '@/api/profiles'
import { useMutation } from '@pinia/colada'
import { email, minLength, pipe, string } from 'valibot'
import { useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
definePage({
  meta: {
    layout: 'public',
    requiresAuth: false,
  },
})
const { t } = useI18n()
const { handleSubmit, values } = useForm<RequestAccessAsExistingCustomerRequest>({
  initialValues: {
    customerNumber: '',
    email: '',
  },
})
const { mutateAsync, status } = useMutation({
  mutation: requestAccessAsExistingCustomer,
})
const submitForm = handleSubmit(async data => {
  await mutateAsync(data)
})
</script>

<template>
  <div class="m-auto w-full rounded-lg bg-white shadow-sm sm:max-w-md md:mt-0 xl:p-0">
    <div class="space-y-4 p-6 sm:p-8 md:space-y-6">
      <h1 class="text-xl leading-tight font-bold tracking-tight text-gray-900 md:text-2xl">
        {{ t('newUser.existingCustomer.title') }}
      </h1>
      <form
        class="space-y-4 md:space-y-6"
        action="#"
      >
        <InputField
          id="customerNumber"
          name="customerNumber"
          type="text"
          :label="t('newUser.existingCustomer.fields.customerNo')"
          :placeholder="t('newUser.existingCustomer.customerNoPlaceholder')"
          :validator="pipe(string(), minLength(1, t('newUser.newCustomer.errorMessages.required')))"
        />
        <InputField
          id="email"
          name="email"
          type="text"
          :placeholder="t('newUser.existingCustomer.fields.eMail')"
          :label="t('newUser.existingCustomer.fields.eMail')"
          :validator="
            pipe(
              string(),
              minLength(1, t('newUser.newCustomer.errorMessages.required')),
              email(t('newUser.newCustomer.errorMessages.email')),
            )
          "
        />
        <div class="flex items-center justify-between">
          <div class="flex items-start">
            <div class="flex h-5 items-center">
              <input
                id="remember"
                aria-describedby="remember"
                type="checkbox"
                class="focus:ring-primary-300 h-4 w-4 rounded-sm border border-gray-300 bg-gray-50 focus:ring-3"
              />
            </div>
            <div class="ml-3 text-sm">
              <label
                for="remember"
                class="text-gray-500"
              >
                {{ t('newUser.rememberMe') }}
              </label>
            </div>
          </div>
        </div>
        <Button
          color="green"
          @click.prevent="submitForm"
        >
          {{ t('newUser.signIn') }}
        </Button>
        <p
          v-if="status === 'error'"
          class="text-sm font-light text-gray-500"
        >
          {{ t('newUser.existingCustomer.errorMsg') }}
        </p>
        <p
          v-else-if="status === 'success'"
          class="text-sm font-light text-gray-500"
        >
          {{
            t('newUser.existingCustomer.successMsg', {
              customerNo: values.customerNumber,
            })
          }}
        </p>
      </form>
    </div>
  </div>
</template>
